import { normalizeSliceUid } from "./segmentationSlices";
import type {
  NativePrediction,
  NativePredictions,
  PredictionSegmentation,
} from "../types/predictions";

type Warn = (message: string) => void;
const noop: Warn = () => {};

type InstanceSliceIndex = Record<string, Record<string, { index: number; s3Key?: string }>>;

export interface ResolvedPredictionMaskSlice {
  sliceId: string;
  seriesId: string;
  sliceIndex: number;
  /** Canonical object key of the slice's file; the overlay finds the viewer image by it. */
  s3Key?: string;
  runs: [number, number][];
}

export interface ResolvedPredictionMaskSegment {
  segmentIndex: number;
  sourceSegmentIndex: number;
  condition: string;
  voxelCount: number;
  slices: ResolvedPredictionMaskSlice[];
}

export interface ResolvedPredictionMask {
  rows: number;
  columns: number;
  segments: ResolvedPredictionMaskSegment[];
}

function isDetected(
  recommended: NativePrediction["recommended_threshold"],
  operatingPoints: NativePrediction["operating_points"] | undefined,
): boolean {
  return !!recommended && operatingPoints?.[recommended] === true;
}

function resolveSlice(
  rawSliceId: string,
  instanceSliceInfoMap: InstanceSliceIndex,
): { seriesId: string; sliceIndex: number; s3Key?: string }[] {
  const uid = normalizeSliceUid(rawSliceId);
  const matches: { seriesId: string; sliceIndex: number; s3Key?: string }[] = [];
  for (const [seriesId, byUid] of Object.entries(instanceSliceInfoMap)) {
    const info = byUid[uid] ?? byUid[`${uid}.dcm`];
    if (info) matches.push({ seriesId, sliceIndex: info.index, s3Key: info.s3Key });
  }
  return matches;
}

function validRuns(segmentation: PredictionSegmentation, runs: [number, number][]): boolean {
  const planeSize = segmentation.rows * segmentation.columns;
  let previousEnd = 0;
  for (const run of runs) {
    if (!Array.isArray(run) || run.length !== 2) return false;
    const [start, length] = run;
    if (!Number.isSafeInteger(start) || !Number.isSafeInteger(length) || start < 0 || length <= 0)
      return false;
    if (start < previousEnd || start + length > planeSize) return false;
    previousEnd = start + length;
  }
  return true;
}

/** Resolve a model segmentation onto the study's concrete series/slice indices. */
export function resolvePredictionMask(
  predictions: NativePredictions,
  instanceSliceInfoMap: InstanceSliceIndex,
  analyzedSeriesId: string | undefined,
  warn: Warn = noop,
): ResolvedPredictionMask | null {
  const candidates = Object.entries(predictions).filter(([, pred]) => {
    const seg = pred?.segmentation;
    return (
      seg?.format === "a2z-rle-v1" &&
      seg.segments.length > 0 &&
      isDetected(pred.recommended_threshold, pred.operating_points)
    );
  });
  if (candidates.length === 0) return null;
  const analyzed = new Set((analyzedSeriesId ?? "").split("+").filter(Boolean));
  const segments: ResolvedPredictionMaskSegment[] = [];
  let rows: number | null = null;
  let columns: number | null = null;
  let nextSegmentIndex = 1;
  for (const [condition, prediction] of candidates) {
    const segmentation = prediction.segmentation!;
    if (
      !Number.isSafeInteger(segmentation.rows) ||
      !Number.isSafeInteger(segmentation.columns) ||
      segmentation.rows <= 0 ||
      segmentation.columns <= 0
    ) {
      warn(`[predictionMask] ${condition} has invalid mask dimensions.`);
      return null;
    }
    if (
      (rows !== null && rows !== segmentation.rows) ||
      (columns !== null && columns !== segmentation.columns)
    ) {
      warn(`[predictionMask] mask-localized findings disagree on source dimensions.`);
      return null;
    }
    rows = segmentation.rows;
    columns = segmentation.columns;

    for (const segment of segmentation.segments) {
      const slices: ResolvedPredictionMaskSlice[] = [];
      for (const slice of segment.slices) {
        if (!validRuns(segmentation, slice.runs)) {
          warn(
            `[predictionMask] ${condition} segment ${segment.segment_index} has invalid RLE on ${slice.slice_id}.`,
          );
          return null;
        }
        const matches = resolveSlice(slice.slice_id, instanceSliceInfoMap);
        if (matches.length === 0) {
          warn(
            `[predictionMask] ${condition} mask slice ${normalizeSliceUid(slice.slice_id)} ` +
              `matches no instance in this study.`,
          );
          continue;
        }
        if (matches.length > 1) {
          warn(
            `[predictionMask] ${condition} mask slice ${normalizeSliceUid(slice.slice_id)} ` +
              `matches more than one series (${matches.map(({ seriesId }) => seriesId).join(", ")}); ` +
              `refusing an arbitrary overlay target.`,
          );
          return null;
        }
        const resolved = matches[0];
        if (analyzed.size > 0 && !analyzed.has(resolved.seriesId)) {
          warn(
            `[predictionMask] ${condition} mask resolved outside analyzed_series_id ` +
              `(series ${resolved.seriesId}); refusing that slice.`,
          );
          continue;
        }
        slices.push({
          sliceId: normalizeSliceUid(slice.slice_id),
          seriesId: resolved.seriesId,
          sliceIndex: resolved.sliceIndex,
          s3Key: resolved.s3Key,
          runs: slice.runs,
        });
      }
      if (slices.length > 0) {
        segments.push({
          segmentIndex: nextSegmentIndex++,
          sourceSegmentIndex: segment.segment_index,
          condition,
          voxelCount: segment.voxel_count,
          slices,
        });
      }
    }
  }

  if (segments.length === 0) {
    warn(`[predictionMask] mask-localized findings contained no renderable mask slices.`);
    return null;
  }
  return {
    rows: rows!,
    columns: columns!,
    segments,
  };
}

/** Expand one RLE plane into the source mask's row-major binary bitmap. */
export function decodePredictionMaskRuns(
  rows: number,
  columns: number,
  runs: readonly [number, number][],
): Uint8Array {
  const bitmap = new Uint8Array(rows * columns);
  for (const [start, length] of runs) bitmap.fill(1, start, start + length);
  return bitmap;
}

/**
 * Source pixel under the centre of target pixel `index`, at the pixel-centre
 * scale the overlay uses (`predictionMaskOverlay.ts`). A centre on the edge
 * between two source pixels goes to the later one, so every target pixel has
 * exactly one source pixel.
 */
function sourcePixel(index: number, source: number, target: number): number {
  return Math.floor(((2 * index + 1) * source) / (2 * target));
}

/** First target pixel whose source pixel is at least `sourceIndex`. */
function firstTargetPixel(sourceIndex: number, source: number, target: number): number {
  return Math.max(0, Math.ceil((sourceIndex * 2 * target - source) / (2 * source)));
}

/**
 * Convert one resolved AI component into the sparse backend annotation shape,
 * on the native `targetRows × targetColumns` grid (ENG-278). The model is fixed
 * at 512² while acquisitions may not be; each native pixel takes the mask value
 * under its centre (nearest neighbour), which is the area inside the overlay's
 * scaled outline before smoothing. Same grid → the mask's own pixels.
 * `sliceKey` gives each slice's index in the annotation volume (ENG-291);
 * without it the metadata `sliceIndex` is used.
 */
export function predictionMaskSegmentToLabelmap(
  segment: ResolvedPredictionMaskSegment,
  rows: number,
  columns: number,
  targetRows = rows,
  targetColumns = columns,
  sliceKey: (slice: ResolvedPredictionMaskSlice) => number = (slice) => slice.sliceIndex,
): Record<string, number[][]> {
  const labelmap: Record<string, number[][]> = {};
  for (const slice of segment.slices) {
    const coordinates: number[][] = [];
    if (targetRows === rows && targetColumns === columns) {
      for (const [start, length] of slice.runs) {
        for (let offset = 0; offset < length; offset++) {
          const index = start + offset;
          const row = Math.floor(index / columns);
          if (row < rows) coordinates.push([row, index % columns]);
        }
      }
    } else if (slice.runs.length > 0) {
      const bitmap = decodePredictionMaskRuns(rows, columns, slice.runs);
      let minRow = rows,
        maxRow = -1,
        minColumn = columns,
        maxColumn = -1;
      for (let index = 0; index < bitmap.length; index++) {
        if (!bitmap[index]) continue;
        const row = Math.floor(index / columns);
        const column = index - row * columns;
        if (row < minRow) minRow = row;
        if (row > maxRow) maxRow = row;
        if (column < minColumn) minColumn = column;
        if (column > maxColumn) maxColumn = column;
      }
      // Scan only the target pixels that can map into the mask's bounding box.
      const rowEnd = Math.min(targetRows, firstTargetPixel(maxRow + 1, rows, targetRows));
      const columnEnd = Math.min(
        targetColumns,
        firstTargetPixel(maxColumn + 1, columns, targetColumns),
      );
      for (let row = firstTargetPixel(minRow, rows, targetRows); row < rowEnd; row++) {
        const sourceOffset = sourcePixel(row, rows, targetRows) * columns;
        for (
          let column = firstTargetPixel(minColumn, columns, targetColumns);
          column < columnEnd;
          column++
        ) {
          if (bitmap[sourceOffset + sourcePixel(column, columns, targetColumns)])
            coordinates.push([row, column]);
        }
      }
    }
    if (coordinates.length > 0) labelmap[String(sliceKey(slice))] = coordinates;
  }
  return labelmap;
}
